import { Sparkles, BookOpen, GraduationCap, Award, ScrollText, ArrowRight, ExternalLink, MessageCircle, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";

const FEATURES = [
  { icon: BookOpen, label: "Search Australian courses and universities" },
  { icon: Award, label: "Explore scholarship opportunities" },
  { icon: GraduationCap, label: "Compare study options and pathways" },
  { icon: Sparkles, label: "Receive AI-powered course recommendations" },
  { icon: MessageCircle, label: "Book a free consultation with an education specialist" },
  { icon: ScrollText, label: "Access admission and application guidance" },
];

/**
 * Reusable promo block linking to https://www.studynet.ai.
 * Variants:
 *   - "feature"  : full featured section (Student Visa page)
 *   - "tool"     : tool-card style (Tools & Assessments page)
 *   - "service"  : highlighted education box (StudyNet Education Services)
 */
export default function StudyNetAIPromo({ variant = "feature" }) {
  if (variant === "tool") {
    return (
      <div data-testid="studynet-ai-tool-card" className="relative bg-white border border-slate-200 hover:border-[#C43A89] transition-all hover:-translate-y-1 hover:shadow-[0_22px_50px_-15px_rgba(196,58,137,0.25)] p-7 md:p-8 overflow-hidden group">
        <span aria-hidden className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#29324A] via-[#C43A89] to-[#C43A89]" />
        <span aria-hidden className="absolute -top-3 right-5 bg-[#C43A89] text-white text-[10px] font-bold tracking-[0.22em] uppercase px-3 py-1 shadow-md">Featured</span>
        <div className="h-14 w-14 rounded-full bg-gradient-to-br from-[#29324A] to-[#C43A89] text-white flex items-center justify-center shadow-lg mb-5">
          <Sparkles className="h-6 w-6" />
        </div>
        <div className="text-[10px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-2">AI-Powered Education</div>
        <h3 className="font-display text-xl md:text-2xl text-[#29324A] leading-snug mb-3">
          StudyNet AI — Australia's AI-Powered Education Platform
        </h3>
        <p className="text-[13px] text-slate-600 leading-relaxed mb-5">
          Discover Australian courses, universities, scholarships and study pathways through our AI-powered education platform. Compare study options, receive personalised recommendations and connect with education specialists for expert guidance.
        </p>
        <ul className="grid grid-cols-2 gap-x-3 gap-y-1.5 mb-6">
          {["Course Search","University Comparison","Scholarship Finder","Study Pathway Explorer","AI Recommendations","Free Consultation"].map((f, i) => (
            <li key={i} className="flex items-start gap-1.5 text-[12px] text-slate-700"><CheckCircle2 className="h-3 w-3 text-emerald-600 mt-0.5 shrink-0" /> {f}</li>
          ))}
        </ul>
        <a
          href="https://studynet.ai/"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="studynet-ai-tool-cta"
          className="inline-flex items-center justify-center gap-2 bg-[#C43A89] hover:bg-[#a22b6e] text-white text-xs font-bold tracking-wider uppercase px-5 py-3 transition-colors btn-shimmer"
        >
          Explore StudyNet AI <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </div>
    );
  }

  if (variant === "service") {
    return (
      <div data-testid="studynet-ai-service-box" className="relative bg-white border-2 border-[#C43A89] p-7 md:p-9 mt-10 overflow-hidden shadow-[0_22px_60px_-18px_rgba(196,58,137,0.3)]">
        <span aria-hidden className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#29324A] via-[#C43A89] to-[#C43A89]" />
        <span aria-hidden className="absolute -top-3 left-7 bg-[#C43A89] text-white text-[10px] font-bold tracking-[0.22em] uppercase px-3 py-1 shadow-md">New · AI-Powered</span>
        <div className="grid md:grid-cols-12 gap-6 md:gap-8 items-center">
          <div className="md:col-span-2 flex md:justify-center">
            <div className="h-16 w-16 rounded-full bg-gradient-to-br from-[#29324A] to-[#C43A89] text-white flex items-center justify-center shadow-lg">
              <Sparkles className="h-7 w-7" />
            </div>
          </div>
          <div className="md:col-span-7">
            <div className="text-[10px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-2">StudyNet AI</div>
            <h3 className="font-display text-xl md:text-2xl text-[#29324A] leading-snug mb-2">
              Your Smart Pathway to Studying in Australia
            </h3>
            <p className="text-[13.5px] text-slate-600 leading-relaxed">
              StudyNet AI helps future students discover courses, compare institutions, explore scholarships and understand admission pathways across Australia. Students can also connect with our experienced education consultants for personalised guidance and support throughout their study journey.
            </p>
          </div>
          <div className="md:col-span-3 flex flex-col gap-2.5">
            <Link
              to="/book-consultation"
              data-testid="studynet-ai-service-book"
              className="inline-flex items-center justify-center gap-1.5 bg-[#29324A] hover:bg-[#1f2638] text-white text-[11px] font-bold tracking-wider uppercase px-4 py-3 transition-colors"
            >
              Book Free Education Consult <ArrowRight className="h-3 w-3" />
            </Link>
            <a
              href="https://studynet.ai/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="studynet-ai-service-visit"
              className="inline-flex items-center justify-center gap-1.5 bg-[#C43A89] hover:bg-[#a22b6e] text-white text-[11px] font-bold tracking-wider uppercase px-4 py-3 transition-colors btn-shimmer"
            >
              Visit StudyNet AI <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </div>
      </div>
    );
  }

  // default — full feature section for Student Visa page
  return (
    <section data-testid="studynet-ai-feature" className="py-16 md:py-20 bg-gradient-to-br from-[#F8F4FB] via-white to-[#FDF2F8] border-y border-slate-200 relative overflow-hidden">
      <div aria-hidden className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-[#C43A89]/8 blur-3xl" />
      <div aria-hidden className="absolute -bottom-24 -left-24 w-80 h-80 rounded-full bg-[#29324A]/8 blur-3xl" />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 bg-white border border-[#C43A89]/20 text-[#C43A89] text-[10px] font-bold tracking-[0.22em] uppercase px-3 py-1.5 mb-5">
              <Sparkles className="h-3 w-3" /> Powered by StudyNet AI
            </div>
            <h2 className="font-display text-3xl md:text-4xl text-[#29324A] tracking-tight leading-tight mb-4">
              Find Courses, Scholarships & Study Pathways with AI.
            </h2>
            <p className="text-[15px] text-slate-700 leading-relaxed mb-6">
              Planning to study in Australia? Explore thousands of courses, compare universities, discover scholarship opportunities and receive personalised study recommendations through our AI-powered education platform. Whether you are looking for undergraduate, postgraduate, vocational or English language programs, <strong className="text-[#29324A]">StudyNet AI</strong> helps you make informed decisions before lodging your student visa application.
            </p>

            <ul className="grid sm:grid-cols-2 gap-2.5 mb-7">
              {FEATURES.map((f, i) => {
                const Icon = f.icon;
                return (
                  <li
                    key={i}
                    data-testid={`studynet-ai-feature-${i}`}
                    className="flex items-start gap-2.5 bg-white border-l-2 border-[#C43A89] px-3 py-2 text-[13px] text-slate-700"
                  >
                    <Icon className="h-4 w-4 text-[#29324A] mt-0.5 shrink-0" />
                    <span>{f.label}</span>
                  </li>
                );
              })}
            </ul>

            <div className="flex flex-wrap gap-3">
              <a
                href="https://studynet.ai/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="studynet-ai-feature-primary"
                className="inline-flex items-center justify-center gap-2 bg-[#C43A89] hover:bg-[#a22b6e] text-white text-xs font-bold tracking-wider uppercase px-6 py-3.5 transition-colors btn-shimmer shadow-lg shadow-[#C43A89]/20"
              >
                Find Courses & Scholarships <ExternalLink className="h-3.5 w-3.5" />
              </a>
              <Link
                to="/book-consultation"
                data-testid="studynet-ai-feature-secondary"
                className="inline-flex items-center justify-center gap-2 bg-white border border-[#29324A] text-[#29324A] hover:bg-[#29324A] hover:text-white text-xs font-bold tracking-wider uppercase px-6 py-3.5 transition-colors"
              >
                Book a Free Consultation <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </div>

          {/* Big visual card */}
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <div className="relative bg-[#29324A] grain text-white p-7 md:p-9 overflow-hidden">
              <span aria-hidden className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#29324A] via-[#C43A89] to-[#C43A89]" />
              <div className="h-14 w-14 rounded-full bg-gradient-to-br from-[#C43A89] to-[#C43A89] text-white flex items-center justify-center shadow-xl mb-5">
                <Sparkles className="h-6 w-6" />
              </div>
              <div className="text-[10px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-2">StudyNet AI</div>
              <h3 className="font-display text-2xl md:text-3xl tracking-tight leading-tight mb-3">
                Start Your Australian Study Journey.
              </h3>
              <p className="text-[13.5px] text-slate-200 leading-relaxed mb-5">
                Thousands of courses · 40+ universities · personalised recommendations · scholarship finder · free consultation booking — all in one AI-powered platform.
              </p>
              <a
                href="https://studynet.ai/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="studynet-ai-feature-explore"
                className="inline-flex items-center justify-center gap-2 w-full bg-[#C43A89] hover:bg-[#a22b6e] text-white text-xs font-bold tracking-wider uppercase py-3.5 transition-colors btn-shimmer"
              >
                Explore Courses & Study Options with AI <ExternalLink className="h-3.5 w-3.5" />
              </a>
              <div className="mt-4 text-[11px] text-slate-300 text-center">Opens in a new tab · studynet.ai/</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
